import React, { useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { 
  HiOutlineHome,
  HiOutlineOfficeBuilding,
  HiOutlineBriefcase,
  HiOutlineClipboardList,
  HiOutlineDocumentText,
  HiOutlineUsers,
  HiOutlineCog,
  HiOutlineMenu,
  HiOutlineX,
  HiOutlineLogout,
  HiOutlineBell,
  HiOutlineCalendar,
  HiOutlineSparkles
} from 'react-icons/hi';
import { useAuth } from '../contexts/AuthContext';
import { User } from '../types';

interface NavigationItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
}

interface SidebarContentProps {
  navigation: NavigationItem[];
  isActive: (href: string) => boolean;
  user: User | null;
  onLogout: () => Promise<void>;
}

const Layout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(false);
  const { user, logout } = useAuth();
  const location = useLocation();

  const navigation: NavigationItem[] = [
    { name: 'Dashboard', href: '/dashboard', icon: HiOutlineHome },
    { name: 'Companies', href: '/companies', icon: HiOutlineOfficeBuilding },
    { name: 'Applications', href: '/applications', icon: HiOutlineClipboardList },
    { name: 'Calendar', href: '/calendar', icon: HiOutlineCalendar },
    { name: 'Resumes', href: '/resumes', icon: HiOutlineDocumentText },
    { name: 'Contacts', href: '/contacts', icon: HiOutlineUsers },
    { name: 'Use AI', href: '/use-ai', icon: HiOutlineSparkles },
    { name: 'Settings', href: '/settings', icon: HiOutlineCog },
  ];

  const isActive = (href: string): boolean => {
    if (href === '/dashboard') {
      return location.pathname === href;
    }
    return location.pathname.startsWith(href);
  };

  const handleLogout = async (): Promise<void> => {
    await logout();
  };

  return (
    <div className="h-screen flex overflow-hidden bg-secondary-50">
      {/* Mobile sidebar */}
      <div className={`lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 flex z-40">
          <div className="fixed inset-0" onClick={() => setSidebarOpen(false)}>
            <div className="absolute inset-0 bg-secondary-600 opacity-75"></div>
          </div>
          
          <div className="relative flex-1 flex flex-col max-w-xs w-full bg-white">
            <div className="absolute top-0 right-0 -mr-12 pt-2">
              <button
                className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                onClick={() => setSidebarOpen(false)}
              >
                <HiOutlineX className="h-6 w-6 text-white" />
              </button>
            </div>
            
            <SidebarContent navigation={navigation} isActive={isActive} user={user} onLogout={handleLogout} />
          </div>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:flex lg:flex-shrink-0">
        <div className="flex flex-col w-64">
          <SidebarContent navigation={navigation} isActive={isActive} user={user} onLogout={handleLogout} />
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-col w-0 flex-1 overflow-hidden">
        {/* Mobile header */}
        <div className="lg:hidden bg-white shadow-sm border-b border-secondary-200">
          <div className="flex items-center justify-between h-16 px-4">
            <button
              className="text-secondary-600 hover:text-secondary-900"
              onClick={() => setSidebarOpen(true)}
            >
              <HiOutlineMenu className="h-6 w-6" />
            </button>
            
            <div className="flex items-center space-x-4">
              <button className="text-secondary-600 hover:text-secondary-900">
                <HiOutlineBell className="h-6 w-6" />
              </button>
              
              <div className="flex items-center space-x-3">
                <img
                  className="h-8 w-8 rounded-full"
                  src={user?.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || 'User')}&background=3b82f6&color=fff`}
                  alt={user?.name}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Main content area */}
        <main className="flex-1 relative overflow-y-auto focus:outline-none">
          <div className="py-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <Outlet />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

// Sidebar content component
const SidebarContent: React.FC<SidebarContentProps> = ({ navigation, isActive, user, onLogout }) => {
  return (
    <div className="flex flex-col h-full bg-white border-r border-secondary-200">
      {/* Logo */}
      <div className="flex items-center h-16 flex-shrink-0 px-4 border-b border-secondary-200">
        <div className="flex items-center">
          <div className="bg-primary-600 rounded-lg p-2">
            <HiOutlineBriefcase className="h-6 w-6 text-white" />
          </div>
          <h1 className="ml-3 text-xl font-semibold text-secondary-900">
            Job Tracker
          </h1>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
        {navigation.map((item) => {
          const Icon = item.icon;
          return (
            <Link
              key={item.name}
              to={item.href}
              className={`
                sidebar-nav-item
                ${isActive(item.href) ? 'active' : ''}
              `}
            >
              <Icon className="h-5 w-5 mr-3" />
              {item.name}
            </Link>
          );
        })}
      </nav>

      {/* User section */}
      <div className="flex-shrink-0 border-t border-secondary-200 p-4">
        <div className="flex items-center space-x-3 mb-3">
          <img
            className="h-10 w-10 rounded-full"
            src={user?.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || 'User')}&background=3b82f6&color=fff`}
            alt={user?.name}
          />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-secondary-900 truncate">
              {user?.name}
            </p>
            <p className="text-sm text-secondary-500 truncate">
              {user?.email}
            </p>
          </div>
        </div>
        
        <button
          onClick={onLogout}
          className="flex items-center w-full px-2 py-2 text-sm text-secondary-600 hover:text-secondary-900 hover:bg-secondary-100 rounded-md transition-colors"
        >
          <HiOutlineLogout className="h-5 w-5 mr-3" />
          Sign out
        </button>
      </div>
    </div>
  );
};

export default Layout; 